import { Box } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRenderCellParams, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridAutocomplete, { Option } from 'components/data-grid/data-grid-autocomplete';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import { renderDataGridOption } from 'components/data-grid/render-data-grid-option';
import Switch from 'components/switch';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isValidDecimal } from 'utils/validate-numbers';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import { useFeedingState } from 'views/production-systems/context/feeding-context';
import { calculateBiomass, calculateMeanWeight, kgToLbs, lbsToKg } from 'views/production-systems/utils/farm-overview';
import CreateFeed from 'views/self-service/features/feed-catalog/components/create-feed';
import RegistrationHeader from '../../../../components/registration-header';
import { useAccountState } from 'contexts/account/account-context';

type Row = {
  pondId: string;
  pondName: string;
  zoneName: string;
  cycleId: number;
  cycleName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  amount: number;
  feed: { id: string; label: string };
  feeder: { id: string; label: string };
};

type Props = {
  rows: Row[];
  feeds: Option[];
  feeders: Option[];
  handleEditing: () => void;
  errorDictionary: { [key: string]: boolean };
  setRows: React.Dispatch<React.SetStateAction<Row[]>>;
  changeErrorDictionary: (errorDict: { [key: string]: boolean }) => void;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.feeding';
const COSTAMARKET_CUSTOMER_NAME = 'COSTAMARKET';

const AddFeedingGrid: React.FC<Props> = ({ rows, setRows, feeds, feeders, handleEditing, errorDictionary, changeErrorDictionary }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const { account } = useAccountState();
  const { feedCodes } = useFeedingState();
  const [enableKG, setEnableKg] = useState<boolean>(true);

  const processRowUpdate = (newRow: GridRowModel<Row>, oldRow?: GridRowModel<Row>) => {
    // If oldRow is not provided, fallback to finding it from rows
    const prevRow = oldRow || rows.find((row) => row.pondId === newRow.pondId);
    let updatedAmount = newRow.amount;
    // Only convert amount if the amount field was changed
    if (prevRow && newRow.amount !== prevRow.amount) {
      updatedAmount = enableKG ? newRow.amount : lbsToKg(newRow.amount);
    }
    const updatedRow: Row = { ...newRow, amount: updatedAmount };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  /// Determine if KG should be enabled based on customer name
  /// this was requeted in this PBI: https://dev.azure.com/Cargill-CargillGlobal/BluViu/_workitems/edit/1981360
  /// A better solution should e discussed to handle this from the admin site for each customer
  /// Right now will be hardcoded since it was requested as a quick fix by Bussiness
  useEffect(() => {
    if (account.company?.customerName === COSTAMARKET_CUSTOMER_NAME) {
      setEnableKg(false);
    }
  }, [account.company?.customerName]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destinationPond` }),
        editable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.cycleName` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'zoneName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.zone` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'currentQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.number` }),
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.meanWeight` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentBm',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.biomass` }, { type: enableKG ? 'kg' : 'lbs' }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => calculateBiomass(row.currentMw, row.currentQty, enableKG),
      } as GridColDef,
      {
        field: 'feed',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.feed` }),
        flex: 1,
        type: 'singleSelect',
        valueOptions: feeds,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={feeds} />,
        sortable: false,
        editable: true,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'feeder',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.feederBrand` }),
        flex: 1,
        type: 'singleSelect',
        valueOptions: feeders,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={feeders} />,
        sortable: false,
        editable: true,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'amount',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.feedQuantity` }, { type: enableKG ? 'kg' : 'lbs' }),
        editable: true,
        type: 'number',
        align: 'right',
        valueGetter: (value: number) => {
          const amount = enableKG ? value : kgToLbs(value);
          return amount;
        },
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-amount`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ],
    [feeds, feeders, enableKG]
  );

  //#region COMPONENTS

  const summaryValues: SummaryItem[] = useMemo(() => {
    let summary = rows.reduce(
      (prev, curr) => {
        return {
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: prev.currentBm + curr.currentBm,
          amount: prev.amount + curr.amount,
          currentMw: 0,
        };
      },
      {
        amount: 0,
        currentQty: 0,
        currentMw: 0,
        currentBm: 0,
      }
    );
    summary.currentMw = calculateMeanWeight(summary.currentBm, summary.currentQty);

    return [
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: summary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.currentMw, // The value remains the number itself. Should not take into account enableKg
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: enableKG ? summary.currentBm : kgToLbs(summary.currentBm), // The value remains the number itself. Should account for enableKg
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem
        type: undefined,
        hidden: true,
      },
      {
        value: enableKG ? summary.amount : kgToLbs(summary.amount),
        type: 'string', // Assuming amount is a general string or number
        hidden: false,
      },
    ];
  }, [rows, enableKG]);

  //#endregion

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <RegistrationHeader />
      <DataGrid
        apiRef={apiRef}
        getRowId={(row) => row.pondId}
        processRowUpdate={processRowUpdate}
        showToolbar
        slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
        rows={rows}
        columns={columns}
        onCellEditStop={({ field }) => {
          if (['feed', 'feeder'].includes(field)) return;
          handleEditing();
        }}
        onRowClick={({ id }) => {
          const { field } = apiRef.current?.state.tabIndex.cell ?? {};
          if (['feed', 'feeder'].includes(field ?? '')) return;
          apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
        }}
        slotProps={{
          toolbar: {
            children: (
              <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
                <CreateFeed feedCodes={feedCodes} />
                <Box display={'flex'} flexDirection={'column'} height={40}>
                  <Switch
                    dark
                    value={enableKG}
                    onChange={() => {
                      setEnableKg((prev) => !prev);
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.add.switchLabel` })}
                  />
                </Box>
              </Box>
            ),
            csvOptions: {
              fileName: `${intl.formatMessage({
                id: 'productionSystems.registrationLog.registrationTypes.feeding',
              })}-${dayjs().format('DD/MM/YYYY')}`,
            },
          },
          footer: {
            summaryValues: summaryValues,
          } as DataGridSummaryProps,
        }}
      />
    </Box>
  );
};

export default React.memo(AddFeedingGrid);
